import { jsPDF } from "jspdf";
import { eur, fmtLongDate, KIND } from "@/lib/format";
import { fileUrl, api } from "@/lib/api";

const GREEN = [97, 162, 41];

async function toDataUrl(url) {
  try {
    const blob = await (await fetch(url)).blob();
    return await new Promise((res) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

const logoData = () => toDataUrl("/brand/logo.png");

// Re-encode as JPEG (max 1200px) to keep the PDF small and support any image format.
async function photoData(item) {
  const src = await toDataUrl(fileUrl(item.file_id));
  if (!src) return null;
  return new Promise((res) => {
    const img = new Image();
    img.onload = () => {
      const k = Math.min(1, 1200 / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * k);
      c.height = Math.round(img.height * k);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      res({ data: c.toDataURL("image/jpeg", 0.8), w: c.width, h: c.height });
    };
    img.onerror = () => res(null);
    img.src = src;
  });
}

function addSignature(doc, c, y) {
  if (y > 235) {
    doc.addPage();
    y = 20;
  }
  doc.setFont("helvetica", "bold").setFontSize(11).setTextColor(...GREEN).text("Firma del cliente", 15, y);
  doc.setDrawColor(200).setLineWidth(0.2).rect(15, y + 3, 85, 32);
  if (c.signature) doc.addImage(c.signature, "PNG", 17, y + 4, 81, 30);
  else doc.setFont("helvetica", "italic").setFontSize(9).setTextColor(140).text("Firma non acquisita", 20, y + 20);
  doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(80);
  doc.text(c.signer_name || "", 15, y + 40);
  if (c.closed_at) doc.text(`Data: ${new Date(c.closed_at).toLocaleString("it-IT")}`, 15, y + 45);
}

async function addPhotos(doc, media) {
  const photos = media.filter((m) => m.content_type?.startsWith("image/")).slice(0, 12);
  if (!photos.length) return;
  const imgs = (await Promise.all(photos.map(photoData))).filter(Boolean);
  if (!imgs.length) return;
  const cellW = 87, cellH = 70, gap = 6;
  imgs.forEach((im, i) => {
    const pos = i % 6;
    if (pos === 0) {
      doc.addPage();
      doc.setFont("helvetica", "bold").setFontSize(12).setTextColor(...GREEN).text("Documentazione fotografica", 15, 18);
    }
    const x = 15 + (pos % 2) * (cellW + gap);
    const y = 26 + Math.floor(pos / 2) * (cellH + gap + 6);
    const k = Math.min(cellW / im.w, cellH / im.h);
    const w = im.w * k, h = im.h * k;
    doc.setFillColor(245, 247, 242).rect(x, y, cellW, cellH, "F");
    doc.addImage(im.data, "JPEG", x + (cellW - w) / 2, y + (cellH - h) / 2, w, h);
    doc.setFont("helvetica", "normal").setFontSize(8).setTextColor(120).text(`Foto ${i + 1}`, x, y + cellH + 4);
  });
}

const money = (n) => eur(n).replace(/\u00a0/g, " ");

export async function buildReportPdf(job) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const susp = job.suspensions || [];
  const suspended = job.status === "sospeso" && susp.length > 0;
  const c = suspended ? susp[susp.length - 1] : job.closure || {};
  const prev = suspended ? susp.slice(0, -1) : susp;
  const series = job.series_id ? (await api.get(`/jobs/${job.id}/series`)).data : [];
  const logo = await logoData();
  doc.setFillColor(15, 23, 16).roundedRect(13, 10, 66, 16, 2, 2, "F");
  if (logo) doc.addImage(logo, "PNG", 15, 12, 62, 12);
  doc.setFont("helvetica", "bold").setFontSize(16).setTextColor(20);
  doc.text("Madonna Store", 195, 17, { align: "right" });
  doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(90);
  doc.text(suspended ? "Rapporto di intervento tecnico · SOSPESO" : "Rapporto di intervento tecnico", 195, 22, { align: "right" });
  doc.setDrawColor(...GREEN).setLineWidth(0.8).line(15, 29, 195, 29);

  let y = 38;
  const row = (label, value) => {
    doc.setFont("helvetica", "bold").setFontSize(9).setTextColor(100).text(label.toUpperCase(), 15, y);
    doc.setFont("helvetica", "normal").setFontSize(10).setTextColor(20);
    const lines = doc.splitTextToSize(String(value || "-"), 130);
    doc.text(lines, 60, y);
    y += 6 * lines.length;
  };
  row("Rif. intervento", job.id?.slice(-8).toUpperCase());
  row("Tipologia", KIND[job.kind]);
  row("Data", series.length ? `Cantiere di ${series.length} giornate · ${fmtLongDate(series[0].date)} - ${fmtLongDate(series[series.length - 1].date)}`
    : job.end_date ? `Dal ${fmtLongDate(job.date)} al ${fmtLongDate(job.end_date)} · ${job.start}-${job.end}` : `${fmtLongDate(job.date)} · ${job.start}-${job.end}`);
  row("Cliente", `${job.client.first_name} ${job.client.last_name}`);
  row("Contatti", `${job.client.phone} · ${job.client.email}`);
  row("Indirizzo", `${job.client.address || ""} ${job.client.city || ""}`);
  row("Apparecchiatura", [job.equipment_label, job.brand_model].filter(Boolean).join(" · "));
  row("Tecnico", c.closed_by || job.technician_name);
  if (suspended) {
    row("Stato", `INTERVENTO SOSPESO · ${c.reason}`);
    row("Pagamento", c.paid ? "Effettuato" : "In sospeso");
  }
  if (prev.length) {
    y += 4;
    doc.setFont("helvetica", "bold").setFontSize(11).setTextColor(...GREEN).text("Interventi precedenti (sospesi)", 15, y);
    y += 6;
    prev.forEach((s) => {
      if (y > 260) { doc.addPage(); y = 20; }
      doc.setFont("helvetica", "bold").setFontSize(9).setTextColor(60)
        .text(`${fmtLongDate(s.date)} · ${s.closed_by || ""} · ${money(s.total)} · pagamento ${s.paid ? "effettuato" : "in sospeso"}`, 15, y);
      y += 4.5;
      doc.setFont("helvetica", "normal").setTextColor(30);
      const t = doc.splitTextToSize(`${s.summary} (sospeso: ${s.reason})`, 180);
      doc.text(t, 15, y);
      y += t.length * 4.5 + 3;
    });
  }

  y += 4;
  doc.setFont("helvetica", "bold").setFontSize(11).setTextColor(...GREEN).text("Lavoro svolto", 15, y);
  y += 6;
  doc.setFont("helvetica", "normal").setFontSize(10).setTextColor(30);
  const sum = doc.splitTextToSize(c.summary || "-", 180);
  doc.text(sum, 15, y);
  y += sum.length * 5 + 6;

  if (series.length) {
    doc.setFont("helvetica", "bold").setFontSize(11).setTextColor(...GREEN).text("Giornate di lavoro", 15, y);
    y += 6;
    series.forEach((d) => {
      if (y > 260) { doc.addPage(); y = 20; }
      doc.setFont("helvetica", "bold").setFontSize(9).setTextColor(60).text(`${fmtLongDate(d.date)} · ${d.start}-${d.end}`, 15, y);
      doc.setFont("helvetica", "normal").setTextColor(30);
      const notes = doc.splitTextToSize(d.id === job.id ? "Giornata di chiusura (vedi lavoro svolto)" : d.day_log?.notes || "-", 125);
      doc.text(notes, 70, y);
      y += Math.max(1, notes.length) * 4.5 + 2;
    });
    y += 4;
    if (y > 200) { doc.addPage(); y = 20; }
  }
  if (c.no_prices) {
    doc.setFont("helvetica", "bold").setFontSize(11).setTextColor(...GREEN).text("Ore della squadra", 15, y);
    y += 6;
    doc.setFont("helvetica", "normal").setFontSize(10).setTextColor(30);
    (c.team_hours || []).forEach((t) => { doc.text(t.name, 18, y); doc.text(`${t.hours} h`, 192, y, { align: "right" }); y += 6; });
    doc.setFont("helvetica", "bold").text("Totale ore", 18, y).text(`${c.hours} h`, 192, y, { align: "right" });
    y += 10;
    if (c.parts?.length) {
      doc.setFont("helvetica", "bold").setFontSize(11).setTextColor(...GREEN).text("Materiali utilizzati", 15, y);
      y += 6;
      doc.setFont("helvetica", "normal").setFontSize(10).setTextColor(30);
      c.parts.forEach((p) => { if (y > 270) { doc.addPage(); y = 20; } doc.text(doc.splitTextToSize(p.name, 150), 18, y); doc.text(`x ${p.qty}`, 192, y, { align: "right" }); y += 6; });
    }
    addSignature(doc, c, y + 6);
  } else {
  doc.setFont("helvetica", "bold").setFontSize(11).setTextColor(...GREEN).text("Dettaglio costi", 15, y);
  y += 4;
  doc.setFillColor(242, 247, 236).rect(15, y, 180, 7, "F");
  doc.setFontSize(9).setTextColor(60).text("Voce", 18, y + 5).text("Q.tà", 130, y + 5).text("Importo", 192, y + 5, { align: "right" });
  y += 12;
  const line = (label, qty, amount) => {
    doc.setFont("helvetica", "normal").setFontSize(10).setTextColor(30);
    doc.text(doc.splitTextToSize(label, 105), 18, y);
    doc.text(String(qty), 130, y);
    doc.text(money(amount), 192, y, { align: "right" });
    y += 7;
  };
  line("Diritto di chiamata", 1, c.call_fee);
  line(`Manodopera (${money(c.hourly_rate)}/h, ${c.billed_minutes} min fatturati)`, `${c.hours} h`, c.time_cost);
  (c.parts || []).forEach((p) => line(`Ricambio: ${p.name} (${money(p.unit_price)} cad.)`, p.qty, p.total));
  doc.setDrawColor(200).setLineWidth(0.2).line(15, y - 3, 195, y - 3);
  if (c.discount > 0) {
    y += 2;
    line("Subtotale", "", c.gross_total);
    line(`Sconto${c.discount_reason ? ` (${c.discount_reason})` : ""}`, "", -c.discount);
    doc.setDrawColor(200).line(15, y - 3, 195, y - 3);
  }
  y += 4;
  doc.setFillColor(...GREEN).rect(115, y - 6, 80, 11, "F");
  doc.setFont("helvetica", "bold").setFontSize(12).setTextColor(255);
  doc.text(suspended ? "TOTALE PARZIALE" : "TOTALE", 119, y + 1).text(money(c.total), 192, y + 1, { align: "right" });
  if (suspended) {
    doc.setFont("helvetica", "bold").setFontSize(10).setTextColor(...(c.paid ? GREEN : [190, 90, 0]))
      .text(c.paid ? "[X] Pagamento effettuato   [ ] Pagamento sospeso" : "[ ] Pagamento effettuato   [X] Pagamento sospeso", 15, y + 1);
  }

  addSignature(doc, c, y + 16);
  }
  await addPhotos(doc, series.length ? series.flatMap((d) => d.media || []) : job.media || []);
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal").setFontSize(8).setTextColor(130);
    doc.text(`Documento generato il ${new Date().toLocaleString("it-IT")}`, 15, 287);
    doc.text(`Pagina ${i} di ${pages}`, 195, 287, { align: "right" });
  }
  return doc.output("blob");
}

export const reportFileName = (job) =>
  `MDN-Rapporto-${job.client.last_name}-${job.date}.pdf`.replace(/\s+/g, "_");

export async function shareReport(job) {
  const blob = await buildReportPdf(job);
  const file = new File([blob], reportFileName(job), { type: "application/pdf" });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    await navigator.share({ files: [file], title: "Rapporto intervento Madonna Store",
      text: `Rapporto intervento del ${job.date} - Madonna Store` });
    return "shared";
  }
  downloadBlob(blob, file.name);
  return "downloaded";
}

export async function buildWeekPdf({ title, days, rows }) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const logo = await logoData();
  if (logo) { doc.setFillColor(15, 23, 16).roundedRect(10, 8, 54, 14, 2, 2, "F"); doc.addImage(logo, "PNG", 12, 10, 50, 10); }
  doc.setFont("helvetica", "bold").setFontSize(14).setTextColor(20).text("Chi lavora dove", 285, 15, { align: "right" });
  doc.setFont("helvetica", "normal").setFontSize(10).setTextColor(90).text(title, 285, 21, { align: "right" });
  const x0 = 12, nameW = 46, colW = (285 - x0 - nameW) / 7;
  let y = 28;
  const header = () => {
    doc.setFillColor(242, 247, 236).rect(x0, y, 285 - x0, 9, "F");
    doc.setFont("helvetica", "bold").setFontSize(9).setTextColor(47, 90, 16);
    doc.text("Cantiere", x0 + 2, y + 6);
    days.forEach((d, i) => doc.text(d, x0 + nameW + i * colW + colW / 2, y + 6, { align: "center" }));
    y += 9;
  };
  header();
  rows.forEach((r) => {
    const nameLines = doc.splitTextToSize(r.name, nameW - 4);
    const h = Math.max(nameLines.length * 4.5, ...r.cells.map((c) => c.length * 5), 5) + 5;
    if (y + h > 198) { doc.addPage(); y = 14; header(); }
    doc.setDrawColor(220).line(x0, y, 285, y);
    doc.setFont("helvetica", "bold").setFontSize(9).setTextColor(r.shop ? 150 : 20, r.shop ? 90 : 20, r.shop ? 0 : 20).text(nameLines, x0 + 2, y + 5);
    r.cells.forEach((people, i) => {
      const cx = x0 + nameW + i * colW;
      doc.line(cx, y, cx, y + h);
      people.forEach((p, k) => {
        const py = y + 5 + k * 5;
        if (p.warn) doc.setFillColor(249, 115, 22).circle(cx + 3, py - 1.2, 1.1, "F");
        doc.setFont("helvetica", p.lead ? "bold" : "normal").setFontSize(8.5).setTextColor(30)
          .text(doc.splitTextToSize(p.name, colW - 7)[0], cx + 5.5, py);
      });
    });
    y += h;
  });
  doc.line(x0, y, 285, y);
  doc.setFont("helvetica", "normal").setFontSize(7.5).setTextColor(120)
    .text("In grassetto il responsabile · pallino arancione = impegnato in due posti con orari sovrapposti", x0, Math.min(y + 6, 205));
  return doc.output("blob");
}


export function downloadBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
